import { GiphyFetch, Rating } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { tabletQuery } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import useContainerDimensions from '@shared/hooks/useContainerDimensions'
import DebouncedSearchInput from '@shared/search/DebouncedSearchInput'
import { FC, lazy, Suspense, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import useGiphyDialog from './useGiphyDialog'

const Grid = lazy(async () => {
  const mod = await import('@giphy/react-components')
  return { default: mod.Grid }
})

const gridContainerStyle: SxProps = {
  overflowY: 'scroll',
  '& .giphy-grid': {
    maxHeight: 400,
  },
}

type FetchOptions = {
  offset: number
  limit: number
  rating?: Rating
}

type GiphySearchContentProps = {
  handleGifClick: (gif: IGif, e: React.SyntheticEvent<HTMLElement, Event>) => void
  id: string
}

const GiphySearchContent: FC<GiphySearchContentProps> = ({ handleGifClick, id }) => {
  const isTablet = useMediaQuery(tabletQuery)

  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearchValue] = useDebounce(searchValue, 300)

  const containerRef = useRef<HTMLDivElement>(null)
  const dimensions = useContainerDimensions(containerRef)

  const { giphyApiKey } = useGiphyDialog(id)

  const giphyApi = new GiphyFetch(giphyApiKey)

  const giphyFetchFn = (offset: number) => {
    const fetchOptions: FetchOptions = { offset, limit: 10, rating: 'g' }

    if (debouncedSearchValue) {
      return giphyApi.search(debouncedSearchValue, fetchOptions)
    } else {
      return giphyApi.trending(fetchOptions)
    }
  }

  const handleSearchInputChange = (searchText: string) => {
    setSearchValue(searchText)
  }

  return (
    <Stack mt={2} spacing={2}>
      <DebouncedSearchInput elementId="giphySearchInput" onChange={handleSearchInputChange} />
      <Box id="giphyGridContainer" height={400} ref={containerRef} sx={gridContainerStyle}>
        <Suspense fallback={null}>
          <Grid
            columns={isTablet ? 3 : 5}
            fetchGifs={giphyFetchFn}
            gutter={8}
            hideAttribution
            key={debouncedSearchValue}
            onGifClick={handleGifClick}
            width={dimensions.width}
          />
        </Suspense>
      </Box>
    </Stack>
  )
}

export default GiphySearchContent
